// Recuerda la última oficina origen que eligió el usuario en el combo de
// /shipments/create y /pre-shipments/create para precargarla en la próxima
// sesión. Solo se persiste el buCode: la BU completa se vuelve a resolver
// contra las oficinas del contrato, de modo que una oficina que ya no está
// asignada al cliente no puede quedarse "pegada" en el formulario.
//
// El scope es login + contrato, no solo login: un mismo operador cambia de
// cliente desde el Header sin volver a loguearse, y las oficinas se comparten
// entre contratos, así que una clave por login arrastraría la oficina del
// cliente anterior.

const STORAGE_PREFIX = 'origin-business-unit'

// Sin login o sin contrato no hay preferencia que guardar: escribir en un
// bucket compartido haría que en las PC de mostrador un usuario heredara la
// oficina del anterior.
export const buildOriginPreferenceKey = (
  login?: string,
  agreementID?: string
): string | undefined =>
  login && agreementID ? `${STORAGE_PREFIX}:${login}:${agreementID}` : undefined

export const readPreferredOriginBuCode = (
  scopeKey?: string
): string | undefined => {
  if (!scopeKey) return undefined

  try {
    return window.localStorage.getItem(scopeKey) ?? undefined
  } catch {
    // Modo privado / storage deshabilitado → sin preferencia.
    return undefined
  }
}

// Pasar buCode undefined borra la preferencia (el usuario limpió el combo).
export const savePreferredOriginBuCode = (
  buCode: string | undefined,
  scopeKey?: string
) => {
  if (!scopeKey) return

  try {
    if (buCode) {
      window.localStorage.setItem(scopeKey, buCode)
    } else {
      window.localStorage.removeItem(scopeKey)
    }
  } catch {
    // Sin storage no hay nada que recordar; no es un error para el usuario.
  }
}

// Devuelve la oficina guardada solo si sigue estando entre las del contrato.
// Si ya no lo está, borra la preferencia para que el combo arranque vacío.
export const resolvePreferredOrigin = <T extends { buCode: string }>(
  options: T[],
  scopeKey?: string
): T | undefined => {
  const saved = readPreferredOriginBuCode(scopeKey)
  if (!saved) return undefined

  const match = options.find((option) => option.buCode === saved)
  if (!match) {
    savePreferredOriginBuCode(undefined, scopeKey)
    return undefined
  }

  return match
}
